import { IContactGroupAddress } from 'contacts/types';
import { IShippingMethod } from 'corporate/types';
import { ExternalProjectAssociationType } from 'projects/enums';
import { IExternalProjectAccount } from 'projects/types';
import { hasActiveExternalProjectAccountLink } from 'projects/utils';
import {
  OrderType,
  PricingStrategy,
  QuoteEntityType,
  QuoteStatus,
  QuoteStatusLabel,
} from 'quotes/enums';

import { ShipSelection } from './ShipSelectionDateFields/enums';
import { ICustomerQuoteUpsertForm } from './types';

interface IGetAlternateDefaultValuesParams {
  entityType: QuoteEntityType;
  isAlternateVersionEnabled: boolean;
  defaultShippingMethod?: IShippingMethod | null;
}

// The alternate-mode starting values of every form that creates a quote/order (Create,
// Duplicate, Add to quote/order), so they cannot drift apart. The shipping method is resolved by
// name from the API, hence passed in; it is left out until it loads.
export const getAlternateDefaultValues = ({
  entityType,
  isAlternateVersionEnabled,
  defaultShippingMethod = null,
}: IGetAlternateDefaultValuesParams): Partial<ICustomerQuoteUpsertForm> => {
  if (!isAlternateVersionEnabled) {
    return {};
  }

  return {
    ...(QuoteEntityType.ORDER === entityType
      ? {
          orderType: OrderType.BILLABLE,
          // NDD earliest is the submission day; blank until the order exists.
          shipSelection: ShipSelection.NO_DELIVERY_DATE,
          earliestAcceptableDate: null,
          ...(defaultShippingMethod ? { shippingMethod: defaultShippingMethod } : {}),
        }
      : {}),
    pricingStrategy: PricingStrategy.EXTERNAL,
  };
};

interface IGetContactDefaultLocationsPrefillParams {
  billToAddress?: Partial<IContactGroupAddress> | null;
  shipToAddress?: Partial<IContactGroupAddress> | null;
  isAlternateVersionEnabled: boolean;
  hasProjectSelected: boolean;
  externalProjectAccounts?: IExternalProjectAccount[] | null;
}

// Whether a contact's default bill-to / ship-to may prefill a create form. Shared by Create and
// Add to quote/order so they cannot drift apart. In alternate mode a default address prefills only
// when it has an AR number and, with a project selected, an active link to that project.
export const getContactDefaultLocationsPrefill = ({
  billToAddress,
  shipToAddress,
  isAlternateVersionEnabled,
  hasProjectSelected,
  externalProjectAccounts,
}: IGetContactDefaultLocationsPrefillParams): {
  prefillBillTo: boolean;
  prefillShipTo: boolean;
} => {
  const isValidForAlternate = (
    address: Partial<IContactGroupAddress> | null | undefined,
    associationType: ExternalProjectAssociationType
  ): boolean =>
    !!address?.accountsReceivableId &&
    (!hasProjectSelected ||
      hasActiveExternalProjectAccountLink(
        externalProjectAccounts,
        address?.accountsReceivableId,
        associationType
      ));

  return {
    prefillBillTo:
      !isAlternateVersionEnabled ||
      isValidForAlternate(billToAddress, ExternalProjectAssociationType.BILL_TO_ACCOUNT),
    prefillShipTo:
      !isAlternateVersionEnabled ||
      isValidForAlternate(shipToAddress, ExternalProjectAssociationType.SHIP_TO_ACCOUNT),
  };
};

interface IGetElementToFocusParams {
  isEdit: boolean;
  entityType: QuoteEntityType;
  isAlternateVersionEnabled: boolean;
  contactChanged: boolean;
  hasContact: boolean;
  hasProject: boolean;
  needsBillToLocation: boolean;
  needsShipToLocation: boolean;
}

export const getElementToFocus = ({
  isEdit,
  entityType,
  isAlternateVersionEnabled,
  contactChanged,
  hasContact,
  hasProject,
  needsBillToLocation,
  needsShipToLocation,
}: IGetElementToFocusParams): keyof ICustomerQuoteUpsertForm | null => {
  const detailsFieldName: keyof ICustomerQuoteUpsertForm =
    QuoteEntityType.QUOTE === entityType ? 'closeConfidence' : 'customerNumber';

  if (contactChanged && !hasContact) {
    return null;
  }

  // Selecting a contact auto-fills the locations only when it carries valid
  // default addresses — an empty one gets focus first, in both modes.
  if (contactChanged && hasContact && needsBillToLocation) {
    return 'billToLocation';
  }

  if (contactChanged && hasContact && needsShipToLocation) {
    return 'shipToLocation';
  }

  // Classic mode lays out Project / Job Name above the contact fields, so a
  // contact change moves focus forward to the fields below instead of
  // pulling it back up to them. Alternate mode continues its cascade below.
  if (contactChanged && hasContact && !isAlternateVersionEnabled) {
    return detailsFieldName;
  }

  if (isEdit) {
    return detailsFieldName;
  }

  if (!hasProject && !hasContact && isAlternateVersionEnabled) {
    return 'contact';
  }

  if (hasProject) {
    return 'jobName';
  }

  return 'project';
};

export const getStatusOptions = (savedStatus: QuoteStatus) =>
  Object.entries(QuoteStatusLabel)
    .filter(
      ([value]) =>
        (QuoteStatus.EDITABLE_BY_CUSTOMER === savedStatus ||
          QuoteStatus.EDITABLE_BY_CUSTOMER !== value) &&
        quoteStatuses.includes(value as QuoteStatus)
    )
    .map(([value, label]) => ({
      value,
      label,
    }));

const quoteStatuses = [
  QuoteStatus.DRAFT,
  QuoteStatus.EDITABLE_BY_CUSTOMER,
  QuoteStatus.LOST,
  QuoteStatus.OPEN,
  QuoteStatus.PUNCHED_OUT,
  QuoteStatus.WON,
];
